import Phaser from 'phaser';
import { isTouchDevice } from '../utils/DeviceUtils';
import { GAME_W, GAME_H } from '../utils/MobileHelper';
import { FONT_BODY } from '../utils/Typography';

// === UTILITAIRES UI TACTILES ===
// Fonctions pour adapter l'UI aux écrans tactiles.
// Toutes les fonctions sont conditionnelles : elles ne font rien sur desktop.

/** Taille minimum des zones tactiles (WCAG) */
const MIN_TOUCH_SIZE = 44;

/**
 * Agrandit la zone interactive d'un objet texte si elle est trop petite
 * pour le tactile. Ajoute un hitArea invisible plus grand si nécessaire.
 * Ne fait rien sur desktop.
 */
export function ensureTouchTarget(obj: Phaser.GameObjects.Text): void {
  if (!isTouchDevice()) return;

  const bounds = obj.getBounds();
  const w = Math.max(bounds.width, MIN_TOUCH_SIZE);
  const h = Math.max(bounds.height, MIN_TOUCH_SIZE);

  // Si la zone est déjà assez grande, rien à faire
  if (bounds.width >= MIN_TOUCH_SIZE && bounds.height >= MIN_TOUCH_SIZE) return;

  // Appliquer un hitArea plus grand centré sur l'objet
  obj.setInteractive({
    hitArea: new Phaser.Geom.Rectangle(
      (bounds.width - w) / 2,
      (bounds.height - h) / 2,
      w, h
    ),
    hitAreaCallback: Phaser.Geom.Rectangle.Contains,
    useHandCursor: true,
  });
}

/**
 * Configure un objet interactif pour afficher une info au tap sur mobile,
 * en remplacement du hover (qui n'existe pas sur tactile).
 * Sur desktop, ne fait rien (le hover existant est conservé).
 *
 * @param obj - L'objet interactif
 * @param tooltip - Le texte Phaser de tooltip
 * @param getTooltipText - Fonction qui retourne le texte à afficher
 * @param getPosition - Fonction optionnelle pour positionner le tooltip
 */
export function addTapTooltip(
  obj: Phaser.GameObjects.GameObject,
  tooltip: Phaser.GameObjects.Text,
  getTooltipText: () => string,
  getPosition?: () => { x: number; y: number },
): (() => void) | undefined {
  if (!isTouchDevice()) return undefined;

  let isShowing = false;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const show = () => {
    if (hideTimer) clearTimeout(hideTimer);
    tooltip.setText(getTooltipText());
    if (getPosition) {
      const pos = getPosition();
      tooltip.setPosition(pos.x, pos.y);
    }
    tooltip.setVisible(true);
    isShowing = true;

    // Auto-masquer après 3 secondes
    hideTimer = setTimeout(() => {
      tooltip.setVisible(false);
      isShowing = false;
    }, 3000);
  };

  const hide = () => {
    if (hideTimer) clearTimeout(hideTimer);
    tooltip.setVisible(false);
    isShowing = false;
  };

  // Sur mobile, le pointerdown toggle le tooltip
  obj.on('pointerdown', () => {
    if (isShowing) {
      hide();
    } else {
      show();
    }
  });

  // Retourner la fonction hide pour nettoyage externe
  return hide;
}

/**
 * Crée un panneau d'info glissant depuis le bas de l'écran (mobile uniquement).
 * Utile pour afficher les détails d'une entité/case en combat.
 */
export function showInfoPanel(
  scene: Phaser.Scene,
  title: string,
  lines: string[],
  options?: { autoHideDelay?: number },
): Phaser.GameObjects.Container | null {
  if (!isTouchDevice()) return null;

  const PANEL_H = 120;
  const PANEL_Y = GAME_H - PANEL_H;

  // Fond du panel
  const bg = scene.add.rectangle(GAME_W / 2, PANEL_Y + PANEL_H / 2, GAME_W - 20, PANEL_H, 0x1a1a2e, 0.95);
  bg.setStrokeStyle(1, 0x3a5a8c);

  // Titre — fontSize +1 mobile pour lisibilité
  const titleText = scene.add.text(GAME_W / 2, PANEL_Y + 12, title, {
    fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
  }).setOrigin(0.5, 0);

  // Contenu — fontSize +1 mobile pour lisibilité
  const bodyText = scene.add.text(GAME_W / 2, PANEL_Y + 32, lines.join('\n'), {
    fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
    lineSpacing: 4,
  }).setOrigin(0.5, 0);

  // Bouton fermer
  const closeBtn = scene.add.text(GAME_W - 30, PANEL_Y + 8, '✕', {
    fontSize: '14px', color: '#e74c3c', fontFamily: FONT_BODY,
  }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });

  const container = scene.add.container(0, 0, [bg, titleText, bodyText, closeBtn]).setDepth(200);

  // Animation d'entrée (glissement depuis le bas)
  container.y = PANEL_H;
  scene.tweens.add({
    targets: container,
    y: 0,
    duration: 200,
    ease: 'Power2',
  });

  const destroy = () => {
    scene.tweens.add({
      targets: container,
      y: PANEL_H,
      duration: 150,
      ease: 'Power2',
      onComplete: () => container.destroy(),
    });
  };

  closeBtn.on('pointerdown', destroy);

  // Auto-hide si configuré
  if (options?.autoHideDelay) {
    scene.time.delayedCall(options.autoHideDelay, destroy);
  }

  return container;
}
